import { motion } from 'framer-motion';
import { CheckCircle, Phone, Award, Users, Globe, Shield } from 'lucide-react';
import { Button } from '@/components/ui/button';
import PageHeader from '@/components/common/PageHeader';
import ConsultationForm from '@/components/common/ConsultationForm';
import TrustSignalStrip from '@/components/common/TrustSignalStrip';
import { usePageMeta } from '@/hooks/useSEO';

const reasons = [
  {
    icon: Award,
    title: '30+ Years Combined Experience',
    description: 'Our team brings decades of immigration expertise to guide your overseas journey.'
  },
  {
    icon: Users,
    title: '10,000+ Successful Placements',
    description: 'Join thousands of satisfied clients who achieved their international career goals.'
  },
  {
    icon: Globe,
    title: '50+ Countries Covered',
    description: 'From Canada to Australia, UK to UAE, we handle immigration to all major destinations.'
  },
  {
    icon: Shield,
    title: 'Transparent & Ethical',
    description: 'No hidden fees, no false promises. Just honest guidance and professional service.'
  },
];

const services = [
  'Express Entry and Provincial Nominee Programs',
  'Skilled Migration to Australia, UK, Germany',
  'EB-5 Investor Immigration to USA',
  'Work Visa Recruitment Assistance',
  'Study Abroad with Work Pathways',
  'Family Sponsorship Programs',
  'Resume Writing and IELTS Coaching',
  'Post-Landing Settlement Support',
];

const WhyZafcoPage = () => {
  // SEO — recruitment positioning page (best proxy for "Global Recruitment" hub)
  usePageMeta(
    'Why Zafco: MEA-Approved Global Recruitment & Immigration Consultants',
    'Why 10,000+ Indians trust Zafco for overseas jobs & PR — MEA-approved (RC: B-0762), 50+ destination countries, transparent fees, ethical guidance since 2015.'
  );

  const openWhatsApp = () => {
    window.open('https://wa.me/919958848998?text=Hello%20Zafco,%20I%20want%20to%20learn%20more%20about%20your%20services', '_blank');
  };

  return (
    <div data-testid="why-zafco-page">
      <PageHeader
        title="Best Overseas Recruitment & Immigration Consultants in Delhi"
        subtitle="India's trusted partner for overseas manpower recruitment and immigration consulting. MEA RC: B-0762/DEL/PER/1000+/5/9148/2015 · 10,000+ placements · 50+ countries."
        breadcrumbs={[{ label: 'Why Zafco' }]}
        keywordsTier={['MEA Approved Since 2015', '10,000+ Placements', '50+ Countries']}
      />

      {/* E-E-A-T trust signals — MEA license, placements, success rate */}
      <TrustSignalStrip />

      {/* Reasons */}
      <section className="py-20">
        <div className="container mx-auto px-6">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {reasons.map((reason, index) => (
              <motion.div
                key={reason.title}
                className="text-center p-6"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <div className="w-20 h-20 rounded-full bg-gradient-to-br from-zafco-blue to-zafco-teal flex items-center justify-center mx-auto mb-6">
                  <reason.icon className="w-10 h-10 text-white" />
                </div>
                <h3 className="text-xl font-bold text-zafco-blue mb-3">{reason.title}</h3>
                <p className="text-gray-600">{reason.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Services & Form */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-16">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl font-bold text-zafco-blue font-montserrat mb-6">
                Comprehensive Immigration Services
              </h2>
              <p className="text-gray-600 mb-8">
                Led by Mr. Zafar Abdul Wahhab, Zafco Human Resource Management provides end-to-end 
                overseas recruitment and immigration assistance for Indian professionals.
              </p>
              <div className="space-y-4">
                {services.map((service, index) => (
                  <motion.div
                    key={service}
                    className="flex items-center gap-3"
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.05 }}
                  >
                    <CheckCircle className="w-6 h-6 text-green-500 flex-shrink-0" />
                    <span className="text-gray-700">{service}</span>
                  </motion.div>
                ))}
              </div>
              <Button 
                onClick={openWhatsApp}
                className="mt-8 bg-gradient-to-r from-zafco-gold to-zafco-orange text-white"
              >
                <Phone className="w-5 h-5 mr-2" />
                Talk to Our Experts
              </Button>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="bg-white rounded-2xl p-8 shadow-xl" data-theme="light"
            >
              <h3 className="text-xl font-bold text-zafco-blue font-montserrat mb-6">
                Get Your Free Assessment
              </h3>
              <ConsultationForm />
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default WhyZafcoPage;
